import { useState } from 'react';

interface ProductThumbProps {
  url?: string | null;
  /** Accessible label / img alt — pass the SKU code. */
  alt: string;
  size?: 'sm' | 'lg';
}

/**
 * Product image thumbnail. Renders the image when a URL is present and loads; otherwise a neutral
 * on-brand placeholder (a muted image glyph). Inert today: listSkus doesn't return `image_url` yet,
 * so every SKU shows the placeholder. Once the MCP populates `image_url` (see the flagged
 * cabana-mcp dependency + CSP note), real product images appear here with no further frontend work. They are served from the
 * cabana-filters-prod-product-images GCS bucket since Shopify was removed on 2026-08-27.
 */
export function ProductThumb({ url, alt, size = 'sm' }: ProductThumbProps) {
  const [failed, setFailed] = useState(false);
  const cls = `thumb thumb--${size}`;

  if (!url || failed) {
    return (
      <span className={`${cls} thumb--placeholder`} role="img" aria-label={`${alt}: no image`}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="8.5" cy="9.5" r="1.6" fill="currentColor" />
          <path d="M4 17l4.5-4.5a1.5 1.5 0 0 1 2 0L16 18M14 15l1.8-1.8a1.5 1.5 0 0 1 2 0L20 15.5"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }

  return (
    <img className={cls} src={url} alt={alt} loading="lazy" onError={() => setFailed(true)} />
  );
}
